"use client";

import { forwardRef, ForwardedRef, useState, RefObject } from "react";
import { useRouter } from "next/navigation";

import { cn } from "@/lib/utils";
import { Nullable } from "@/types/common";
import { Generation as TGeneration } from "@/types/thread";
import PromptViewer from "./PromptViewer";
import GenerationConfig from "./GenerationConfig";
import GenerationCards from "./GenerationCards";
import GenerationControls from "./GenerationControls";
import ImageViewer from "@/components/common/ImageViewer";
import SourceImages from "./SourceImages";
// import ViewComments from "./ViewComments";
// import UsePrompt from "./UsePrompt";

interface GenerationProps extends TGeneration {
  isSubscribed: boolean;
  editEnabled: boolean;
  showFeedControls: boolean;
  className?: string;
  ref?: ForwardedRef<HTMLDivElement>;
  generationRefs?: { [key: number]: RefObject<HTMLDivElement> };
}

const Generation = forwardRef<HTMLDivElement, GenerationProps>((props, ref) => {
  const router = useRouter();
  // const dispatch = useAppDispatch();
  const isPrivate = Boolean(props.editEnabled);
  // const isFeed = Boolean(props.showFeedControls);
  // const modelUsed = props.modelUsed;

  const [selectedImageIndex, setSelectedImageIndex] =
    useState<Nullable<number>>(null);

  // const handleViewThread = () => {
  //   dispatch(setRoomUUID(null));
  //   dispatch(setGenerations([]));
  //   router.push(`/idea/${props.roomUUID}`);
  // };

  // const handleUpgradeDialogOpen = () => {
  //   if (!props.isSubscribed) {
  //     dispatch(
  //       addAlertDialog({
  //         title: "Subscription Required",
  //         description: `You need a Premium or Professional subscription to ${
  //           modelUsed === TransformationType.Enhance ? "enhance" : "create HD"
  //         } generations!`,
  //         nextButtonText: "Upgrade",
  //         type: AlertDialogType.SubscriptionRequired,
  //       })
  //     );
  //   }
  // };

  const handleOpenImageViewer = (imageIndex: number) => {
    if (props.slug) {
      const imageId = props.images[imageIndex].id;
      router.push(`/gen/${props.slug}-${props.id}/?highlighted=${imageId}`);
    } else {
      setSelectedImageIndex(imageIndex);
    }
  };

  const handleCloseImageViewer = () => {
    setSelectedImageIndex(null);
  };

  return (
    <div
      className={cn(
        "tw:flex tw:flex-col tw:items-center tw:gap-2 tw:max-w-full",
        props.className
      )}
      ref={ref}
      id={props.id.toString()}
    >
      <SourceImages
        rawImageUrl={props.rawImageUrl}
        inspirationImages={props.inspirationImageUrls}
        isSubscribed={props.isSubscribed}
        modelUsed={props.modelUsed}
      />
      <div className="tw:w-full tw:max-w-[605px] tw:mx-auto tw:pt-4">
        <GenerationConfig
          modelUsed={props.modelUsed}
          aspectRatio={props.aspectRatioUsed}
        />
      </div>
      <div className="tw:w-[605px] tw:max-w-full tw:text-left tw:lg:max-w-[605px]">
        <PromptViewer rawPrompt={props.prompt} textPrompt={props.textPrompt} />
      </div>
      <div>
        <GenerationCards
          images={props.images}
          editEnabled={props.editEnabled}
          onImageClick={handleOpenImageViewer}
          modelUsed={props.modelUsed}
        />
      </div>
      <div className="tw:w-[calc(100vw-24px)] tw:max-w-full tw:mx-auto tw:lg:w-[605px]">
        <GenerationControls
          id={props.id}
          threadUUID={props.roomUUID}
          createdAt={props.createdAt}
          images={props.images}
          viewMode={props.viewMode}
          feedbackRating={props.feedbackRating}
          feedbackComment={props.feedbackComment}
          feedbackTooSlow={props.feedbackTooSlow}
          feedbackWrongOutput={props.feedbackWrongOutput}
          isPrivate={isPrivate}
        />
      </div>
      {/* {isFeed && (
        <div className="tw:w-full tw:max-w-[605px] tw:flex tw:items-center tw:justify-end tw:gap-2 tw:lg:gap-4">
          <ViewComments
            roomUUID={props.roomUUID}
            totalComments={props.commentCount}
          />
          <ShareButton
            url={`${process.env.NEXT_PUBLIC_BASE_URL}/idea/${props.roomUUID}`}
          />
          <UsePrompt prompt={props.textPrompt || props.prompt?.toString()} />
          <Button variant="outlined" size="small" onClick={handleViewThread}>
            {isMobile ? "View" : "View Thread"}
          </Button>
        </div>
      )} */}
      {selectedImageIndex !== null && (
        <ImageViewer
          initialSelection={selectedImageIndex}
          images={
            props.isSubscribed
              ? props.images
              : props.images.length === 4
              ? props.images.slice(0, 2)
              : props.images // TODO: Backend should only lock 2 images instead of 3
          }
          onClose={handleCloseImageViewer}
          roomUUID={props.roomUUID}
        />
      )}
    </div>
  );
});

export default Generation;

Generation.displayName = "Generation";
